import React, { useMemo, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3030/api';

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: 'idle', message: '' });

  const isSubmitting = status.type === 'loading';

  const supportSummary = useMemo(
    () => [
      { label: 'Support', value: 'dailtpr@gmail.com', href: 'mailto:dailtpr@gmail.com' },
      { label: 'Phone', value: '+91 9176126422', href: 'tel:+919176126422' },
      { label: 'WhatsApp', value: '+91 9176687956', href: 'https://wa.me/919176687956' },
    ],
    []
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[name];
      return updated;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedPhone = formData.phone.trim();
    const trimmedMessage = formData.message.trim();

    const validationErrors = {};

    if (!trimmedName) {
      validationErrors.name = 'Please enter your name.';
    }

    if (!trimmedEmail) {
      validationErrors.email = 'Please enter your email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      validationErrors.email = 'Please enter a valid email address.';
    }

    if (!trimmedPhone) {
      validationErrors.phone = 'Please enter your phone number.';
    } else if (!/^[0-9+()\s-]{7,15}$/.test(trimmedPhone)) {
      validationErrors.phone = 'Please enter a valid phone number.';
    }

    if (!trimmedMessage) {
      validationErrors.message = 'Please share a short message.';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setStatus({ type: 'error', message: 'Please fix the highlighted errors before submitting.' });
      return;
    }

    setErrors({});
    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/contact-enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          phone: trimmedPhone,
          message: trimmedMessage,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.message || 'We could not send your enquiry. Please try again.');
      }

      setFormData({ name: '', email: '', phone: '', message: '' });
      setErrors({});
      setStatus({ type: 'success', message: 'Thanks for reaching out! Our support team will get back to you shortly.' });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error?.message || 'Something went wrong while sending your enquiry. Please try again later.',
      });
    }
  };

  return (
    <section className="bg-white py-24">
      <div className="mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-6">
          <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-600">
            Contact Us
          </span>
          <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">Let&apos;s plan your next delivery</h2>
          <p className="text-base text-slate-600">
            Call us with your order requirements or partnership ideas. We respond immediately during service hours and make sure every request is acknowledged, coordinated, and billed with the shop&apos;s invoice.
          </p>
          <div className="grid gap-4 text-sm text-slate-600">
            {supportSummary.map((item) => (
              <p key={item.label}>
                <strong className="font-semibold text-slate-900">{item.label}:</strong>{' '}
                {item.href ? (
                  <a href={item.href} className="transition hover:text-emerald-600">
                    {item.value}
                  </a>
                ) : (
                  item.value
                )}
              </p>
            ))}
            <div className="flex gap-3 pt-2">
              <a
                href="https://wa.me/919176687956"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-emerald-200 transition hover:-translate-y-0.5 hover:bg-emerald-600"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                  <path d="M12 .5C5.65.5.5 5.65.5 12c0 2.04.53 3.98 1.52 5.72L.5 23.5l5.92-1.53A11.44 11.44 0 0012 23.5c6.35 0 11.5-5.15 11.5-11.5S18.35.5 12 .5zm0 21c-1.72 0-3.38-.45-4.85-1.31l-.35-.21-3.51.9.94-3.42-.23-.35A9.46 9.46 0 012.5 12C2.5 6.76 6.76 2.5 12 2.5S21.5 6.76 21.5 12 17.24 21.5 12 21.5zm5.2-6.24c-.29-.15-1.72-.85-1.99-.95-.27-.1-.47-.15-.67.15-.2.29-.77.95-.94 1.15-.17.2-.35.22-.64.07-.29-.15-1.25-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.05-.17-.29-.02-.45.13-.6.13-.13.29-.35.44-.52.15-.17.2-.29.3-.49.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01s-.52.07-.79.37c-.27.29-1.04 1.02-1.04 2.49 0 1.47 1.06 2.89 1.2 3.08.15.2 2.08 3.18 5.04 4.46 1.77.76 2.46.82 3.34.69.54-.08 1.72-.7 1.97-1.38.25-.68.25-1.26.17-1.38-.07-.12-.26-.2-.55-.35z" />
                </svg>
                WhatsApp Support
              </a>
              <a
                href="https://instagram.com/dialtpr"
                className="inline-flex items-center justify-center rounded-full border border-emerald-200 p-2 text-emerald-600 transition hover:bg-emerald-50"
                aria-label="Instagram"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5">
                  <rect x="3" y="3" width="18" height="18" rx="5" />
                  <circle cx="12" cy="12" r="3.5" />
                  <circle cx="17" cy="7" r="1" fill="currentColor" stroke="none" />
                </svg>
              </a>
              <a
                href="https://www.youtube.com/channel/UCUYu_0ytc0K4r-IYMFqo1Mw"
                className="inline-flex items-center justify-center rounded-full border border-emerald-200 p-2 text-emerald-600 transition hover:bg-emerald-50"
                aria-label="YouTube"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                  <path d="M21.6 7.2a2.4 2.4 0 00-1.69-1.7C18.08 5 12 5 12 5s-6.08 0-7.91.5A2.4 2.4 0 002.4 7.2 25.94 25.94 0 002 12a25.94 25.94 0 00.4 4.8 2.4 2.4 0 001.69 1.7C5.92 19 12 19 12 19s6.08 0 7.91-.5a2.4 2.4 0 001.69-1.7 25.94 25.94 0 00.4-4.8 25.94 25.94 0 00-.4-4.8zM10 15.27V8.73L15.5 12z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
        <form
          className="space-y-6 rounded-3xl border border-emerald-100 bg-emerald-50/40 p-8 shadow-lg shadow-emerald-100"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="space-y-1">
            <label htmlFor="name" className="text-sm font-semibold text-slate-900">
              Name
            </label>
            <input
              id="name"
              type="text"
              name="name"
              placeholder="Enter your name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 ${
                errors.name
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                  : 'border-emerald-200 focus:border-emerald-400 focus:ring-emerald-200'
              }`}
              required
            />
            {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
          </div>
          <div className="space-y-1">
            <label htmlFor="email" className="text-sm font-semibold text-slate-900">
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              placeholder="you@company.com"
              value={formData.email}
              onChange={handleChange}
              className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 ${
                errors.email
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                  : 'border-emerald-200 focus:border-emerald-400 focus:ring-emerald-200'
              }`}
              required
            />
            {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
          </div>
          <div className="space-y-1">
            <label htmlFor="phone" className="text-sm font-semibold text-slate-900">
              Phone
            </label>
            <input
              id="phone"
              type="tel"
              name="phone"
              placeholder="Your mobile number"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 ${
                errors.phone
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                  : 'border-emerald-200 focus:border-emerald-400 focus:ring-emerald-200'
              }`}
              required
            />
            {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
          </div>
          <div className="space-y-1">
            <label htmlFor="message" className="text-sm font-semibold text-slate-900">
              Message
            </label>
            <textarea
              id="message"
              rows="4"
              name="message"
              placeholder="Tell us what you&apos;re looking to achieve..."
              value={formData.message}
              onChange={handleChange}
              className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 ${
                errors.message
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                  : 'border-emerald-200 focus:border-emerald-400 focus:ring-emerald-200'
              }`}
              required
            />
            {errors.message && <p className="text-xs text-red-500">{errors.message}</p>}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full rounded-full px-6 py-3 text-sm font-semibold text-white shadow-md shadow-emerald-200 transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-400 ${
              isSubmitting ? 'bg-emerald-300 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-600'
            }`}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
          {status.message && (
            <p
              className={`text-sm ${
                status.type === 'success'
                  ? 'text-emerald-600'
                  : status.type === 'error'
                  ? 'text-red-500'
                  : 'text-slate-600'
              }`}
            >
              {status.message}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
